// Where SpanNotes offers "Comment" for a selection: by the pointer where it
// let go (else, for a selection made with keys, at its moving end), with a
// small mark at the other end for whoever looks for it there.

export interface Point {
  x: number;
  y: number;
}
/** A caret's box, as a collapsed range reports it (viewport coordinates). */
export interface Caret {
  left: number;
  top: number;
  bottom: number;
}
/** How close the mark may come to the button before it is left out (it would only crowd it). */
export const MARK_MIN = 32;
/** The viewport's margin, and the gap between a point and what is placed by it. */
const EDGE = 16;
const GAP = 6;

/**
 * The button's top-left, and the mark's centre (null when it would sit by
 * the button): the button just below and right of `pointer`, or of the
 * selection's moving end (`focus`) without one, flipped above it when it
 * would leave the viewport; the mark at the other end (`anchor`).
 */
export function offerPlaces(input: { pointer: Point | null; focus: Caret; anchor: Caret; viewport: { w: number; h: number }; button: { w: number; h: number } }): {
  button: { left: number; top: number };
  mark: Point | null;
} {
  const { pointer, focus, anchor, viewport, button } = input;
  const at = pointer ?? { x: focus.left, y: focus.bottom };
  const left = Math.max(EDGE, Math.min(at.x + GAP, viewport.w - button.w - EDGE));
  const below = at.y + GAP;
  const top = below + button.h <= viewport.h - EDGE ? below : Math.max(EDGE, at.y - GAP - button.h);
  // The other end: where the selection started, at its first line's middle.
  const mark = { x: anchor.left, y: (anchor.top + anchor.bottom) / 2 };
  const near = Math.hypot(mark.x - (left + button.w / 2), mark.y - (top + button.h / 2)) < MARK_MIN + button.w / 2;
  const seen = mark.x >= 0 && mark.y >= 0 && mark.x <= viewport.w && mark.y <= viewport.h;
  return { button: { left, top }, mark: near || !seen ? null : mark };
}
